import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  ClipboardList, Plus, FolderOpen,
  CheckCircle2, Save, Info, Upload,
} from 'lucide-react';
import HistoryPage from './HistoryPage';
import UploadPage from './UploadPage';
import DraftsPage from './DraftsPage';
import ReceiptDetailPage from './ReceiptDetailPage';
import PortalLayout from '../../shared/components/PortalLayout';
import { AMBER } from '../../styles/tokens';
import client from '../../api/client';

const TOAST_ICONS = {
  submitted:   <CheckCircle2 size={16} />,
  draft:       <Save size={16} />,
  discarded:   <Info size={16} />,
  resubmitted: <Upload size={16} />,
};

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 76, right: 16,
      background: '#1e293b', color: '#fff',
      borderRadius: 10, padding: '11px 18px',
      fontSize: 14, fontWeight: '600',
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      zIndex: 500, animation: 'fadeIn 0.2s ease',
      fontFamily: 'Inter, sans-serif',
      maxWidth: 'calc(100vw - 32px)',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {TOAST_ICONS[toast.type] || <Info size={16} />}
      {toast.message}
    </div>
  );
}

export default function EmployeePortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [receipts, setReceipts] = useState([]);
  const [toast, setToast]   = useState(null);

  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }

  const fetchReceipts = useCallback(async (params = {}) => {
    try {
      const res = await client.get('/api/receipts', { params: { ...params, view: 'my' } });
      setReceipts(res.data);
    } catch (err) {
      console.error('Failed to load receipts:', err);
    }
  }, []);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  function handleNewExpense() {
    navigate('/employee/upload');
  }

  function handleResumeDraft() { navigate('/employee/drafts'); }

  async function handleSubmitted(count, serverReceipts) {
    if (serverReceipts?.length) {
      setReceipts((prev) => {
        const updated = prev.map((r) => serverReceipts.find((u) => u.id === r.id) ?? r);
        const newIds = new Set(prev.map((r) => r.id));
        const added = serverReceipts.filter((u) => !newIds.has(u.id));
        return [...updated, ...added];
      });
    }
    await fetchReceipts();
    showToast('submitted', `${count} recibo${count !== 1 ? 's' : ''} enviado${count !== 1 ? 's' : ''} para revisión`);
    navigate('/employee');
  }

  async function handleSavedDraft(count, serverReceipts) {
    if (serverReceipts?.length) {
      setReceipts((prev) => {
        const updated = prev.map((r) => serverReceipts.find((u) => u.id === r.id) ?? r);
        const newIds = new Set(prev.map((r) => r.id));
        const added = serverReceipts.filter((u) => !newIds.has(u.id));
        return [...updated, ...added];
      });
    }
    await fetchReceipts();
    showToast('draft', `${count} recibo${count !== 1 ? 's' : ''} guardado${count !== 1 ? 's' : ''} como borrador`);
    navigate('/employee');
  }

  function handleDiscarded() {
    showToast('discarded', 'Cambios descartados');
    navigate('/employee');
  }

  async function handleResubmitted() {
    await fetchReceipts();
    showToast('resubmitted', 'Recibo reenviado para revisión');
    navigate('/employee');
  }

  const hasDraft = receipts.some((r) => r.status === 'draft');

  // Determine active tab from URL
  const currentPath = location.pathname;
  const isHistory = currentPath === '/employee' || currentPath === '/employee/';
  const isUpload = currentPath.startsWith('/employee/upload');
  const isDrafts = currentPath.startsWith('/employee/drafts');

  return (
    <PortalLayout activeModule="gastos">
      {/* Page content */}
      <div className="has-bottom-bar">
        <Routes>
          <Route index element={
            <HistoryPage
              receipts={receipts}
              onNewExpense={handleNewExpense}
              onResumeDraft={handleResumeDraft}
              onEditSentBack={(r) => navigate(`/employee/receipt/${r.id}`)}
              onViewReceipt={(r) => navigate(`/employee/receipt/${r.id}`)}
              onRefresh={fetchReceipts}
            />
          } />
          <Route path="upload" element={
            <UploadPage
              initialItems={null}
              editReceipt={null}
              onSubmitted={handleSubmitted}
              onSavedDraft={handleSavedDraft}
              onDiscarded={handleDiscarded}
              onResubmitted={handleResubmitted}
              onBack={() => navigate('/employee')}
            />
          } />
          <Route path="drafts" element={
            <DraftsPage
              initialDrafts={receipts.filter((r) => r.status === 'draft')}
              onBack={() => { fetchReceipts(); navigate('/employee'); }}
              onToast={showToast}
            />
          } />
          <Route path="receipt/:id" element={
            <ReceiptDetailPage onToast={showToast} />
          } />
        </Routes>
      </div>

      {/* Bottom tab bar — mobile only */}
      <nav className="bottom-tab-bar">
        <button
          className={`bottom-tab-btn${isHistory ? ' active' : ''}`}
          onClick={() => navigate('/employee')}
        >
          <ClipboardList size={22} className="bottom-tab-btn-icon" />
          Reportes
        </button>
        <button
          className={`bottom-tab-btn${isUpload ? ' active' : ''}`}
          onClick={handleNewExpense}
        >
          <Plus size={22} className="bottom-tab-btn-icon" />
          Nuevo
        </button>
        <button
          className={`bottom-tab-btn${isDrafts ? ' active' : ''}`}
          onClick={handleResumeDraft}
          style={{ position: 'relative' }}
        >
          {hasDraft && (
            <span style={{
              position: 'absolute', top: 6, right: 'calc(50% - 18px)',
              width: 8, height: 8, borderRadius: '50%',
              background: AMBER, border: '1.5px solid #0a0a0a',
            }} />
          )}
          <FolderOpen size={22} className="bottom-tab-btn-icon" />
          Borradores
        </button>
      </nav>

      <Toast toast={toast} />
    </PortalLayout>
  );
}
